// shared/src/translators/macro-grounding.ts
// Part B of docs/macro-grounding-validator-2026-06-11-brief-revision.md.
// PURE (no I/O) derivation of YoY/MoM from FRED observations + a ground-truth
// set the macro stages feed to the LLM (labeled real rates, so it never recalls
// from training memory) AND the numeric-grounding validator checks against.
//
// Root cause RC2 of the 2026-06-10 incident: the macro snapshot handed the LLM
// raw index levels (CPILFESL ≈ 326.1) with no derived rate, so the model
// supplied "2.9% YoY" from memory and invented a "4.2% consensus" to frame it.
// Deriving the YoY in code gives a verifiable number. [[feedback_no_numeric_anchors_to_llm]].

import { fredSeriesLabel, type FredSeriesTransform } from '../resolvers/fred-series-label.js';
import type { MacroActualGround } from './numeric-grounding-validate.js';

export interface FredObservation {
  readonly date:  string;          // YYYY-MM-DD
  readonly value: number | null;
}

export interface MacroGroundTruth {
  readonly series_id:    string;
  readonly label:        string;
  readonly transform:    FredSeriesTransform;
  readonly unit:         string;
  readonly latest_value: number;
  readonly latest_date:  string;
  /** YoY % change vs the ~12-month-prior observation; null for level series or when no prior obs. */
  readonly yoy_pct:      number | null;
  /** Period-over-period (e.g. MoM) % change vs the immediately prior observation; null when absent. */
  readonly mom_pct:      number | null;
  /**
   * The single canonical figure a reader/LLM would cite for this series:
   * yoy_pct for price indices, latest_value for rates/levels. This is what the
   * validator grounds prose numbers against.
   */
  readonly canonical_value: number;
}

const YOY_TARGET_DAYS    = 365;
const YOY_TOLERANCE_DAYS = 45;   // accept the closest obs within ±45d of the 12-month anchor

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Find the observation closest to `targetMs`, within `toleranceDays`, among
 * non-null observations (excluding the latest). Returns null when none qualify.
 */
function findAnchor(
  obs:           ReadonlyArray<FredObservation>,
  latestDate:    string,
  targetMs:      number,
  toleranceDays: number,
): FredObservation | null {
  let best:     FredObservation | null = null;
  let bestDiff: number                 = Infinity;
  const tolMs = toleranceDays * 86_400_000;
  for (const o of obs) {
    if (o.value === null || !Number.isFinite(o.value)) continue;
    if (o.date === latestDate) continue;
    const ms = Date.parse(`${o.date}T00:00:00Z`);
    if (!Number.isFinite(ms)) continue;
    const diff = Math.abs(ms - targetMs);
    if (diff < bestDiff && diff <= tolMs) {
      bestDiff = diff;
      best     = o;
    }
  }
  return best;
}

/**
 * Derive grounded figures for one FRED series from its observations.
 *
 * @param seriesId     FRED series ID (must be in FRED_SERIES_LABELS, else null).
 * @param observations DESC-sorted (latest first) per fetchFredSeries sort_order=desc.
 *                     Tolerates ASC too (we locate latest by max date).
 * @returns the ground-truth record, or null when the series is uncurated or has
 *          no usable latest value (never fabricates).
 */
export function deriveMacroGroundTruth(
  seriesId:     string,
  observations: ReadonlyArray<FredObservation>,
): MacroGroundTruth | null {
  const meta = fredSeriesLabel(seriesId);
  if (!meta) return null;

  // Locate the latest non-null observation by max date (robust to sort order).
  let latest: FredObservation | null = null;
  let latestMs = -Infinity;
  for (const o of observations) {
    if (o.value === null || !Number.isFinite(o.value)) continue;
    const ms = Date.parse(`${o.date}T00:00:00Z`);
    if (Number.isFinite(ms) && ms > latestMs) {
      latestMs = ms;
      latest   = o;
    }
  }
  if (!latest || latest.value === null) return null;

  // MoM (period-over-period): the next-most-recent non-null obs.
  let prev: FredObservation | null = null;
  let prevMs = -Infinity;
  for (const o of observations) {
    if (o.value === null || !Number.isFinite(o.value)) continue;
    if (o.date === latest.date) continue;
    const ms = Date.parse(`${o.date}T00:00:00Z`);
    if (Number.isFinite(ms) && ms > prevMs && ms < latestMs) {
      prevMs = ms;
      prev   = o;
    }
  }
  const mom_pct =
    prev && prev.value !== null && prev.value !== 0
      ? round2((latest.value / prev.value - 1) * 100)
      : null;

  // YoY only for price-index series (transform 'yoy').
  let yoy_pct: number | null = null;
  if (meta.transform === 'yoy') {
    const anchor = findAnchor(observations, latest.date, latestMs - YOY_TARGET_DAYS * 86_400_000, YOY_TOLERANCE_DAYS);
    if (anchor && anchor.value !== null && anchor.value !== 0) {
      yoy_pct = round2((latest.value / anchor.value - 1) * 100);
    }
  }

  const canonical_value = meta.transform === 'yoy' && yoy_pct !== null ? yoy_pct : latest.value;

  return {
    series_id:    seriesId,
    label:        meta.label,
    transform:    meta.transform,
    unit:         meta.unit,
    latest_value: latest.value,
    latest_date:  latest.date,
    yoy_pct,
    mom_pct,
    canonical_value,
  };
}

/**
 * Build ground-truth records for a map of seriesId → observations.
 * Uncurated / unusable series are skipped (not fabricated).
 */
export function buildMacroGroundTruth(
  seriesObs: Readonly<Record<string, ReadonlyArray<FredObservation>>>,
): MacroGroundTruth[] {
  const out: MacroGroundTruth[] = [];
  for (const [id, obs] of Object.entries(seriesObs)) {
    const gt = deriveMacroGroundTruth(id, obs);
    if (gt) out.push(gt);
  }
  return out;
}

/**
 * Flat map of canonical-figure → number, keyed by series ID, for the numeric
 * grounding validator. The validator binds a prose number to a label, looks up
 * the series, and compares against this canonical figure.
 */
export function groundTruthFigures(
  records: ReadonlyArray<MacroGroundTruth>,
): Readonly<Record<string, number>> {
  const out: Record<string, number> = {};
  for (const r of records) out[r.series_id] = r.canonical_value;
  return out;
}

// How each series is named in thesis prose, so the validator's D1 actual-check
// can bind a number to the grounded figure. Domain knowledge lives here (not in
// the generic validator). Only the inflation family (the incident surface) has
// aliases today; extend as needed.
const MACRO_PROSE_ALIASES: Readonly<Record<string, RegExp>> = {
  CPILFESL: /\bcore\s+(?:cpi|inflation)\b/i,
  CPIAUCSL: /\bheadline\s+(?:cpi|inflation)\b/i,
  PCEPILFE: /\bcore\s+pce\b/i,
};

// ±tolerance (in the grounded figure's unit) before a prose number is flagged
// as a mismatch. 0.3pp for YoY inflation rates: tight enough to catch a
// headline↔core swap (4.2 vs 2.9 = 1.3pp), loose enough for rounding.
const YOY_MISMATCH_TOLERANCE_PP = 0.3;

/**
 * Build the validator's D1 grounds from derived ground-truth records: one entry
 * per series that (a) has a prose alias and (b) has a usable grounded YoY figure.
 */
export function buildMacroActualGrounds(
  records: ReadonlyArray<MacroGroundTruth>,
): MacroActualGround[] {
  const out: MacroActualGround[] = [];
  for (const r of records) {
    const aliasRe = MACRO_PROSE_ALIASES[r.series_id];
    if (!aliasRe) continue;
    if (r.transform === 'yoy' && r.yoy_pct === null) continue; // no grounded figure
    // own_acceptable = every figure that is a CORRECT citation for this series:
    // the canonical (YoY) + the MoM, so "Core CPI rose 0.2% MoM" is not flagged.
    const own = [r.canonical_value, ...(r.mom_pct !== null ? [r.mom_pct] : [])];
    out.push({
      series_id:      r.series_id,
      label:          r.label,
      grounded_value: r.canonical_value,
      own_acceptable: own,
      aliasRe,
      tolerance:      r.transform === 'yoy' ? YOY_MISMATCH_TOLERANCE_PP : Math.abs(r.canonical_value) * 0.01 + 0.01,
    });
  }
  return out;
}

/**
 * Render the ground-truth set as an LLM prompt block: labeled, with the
 * canonical figure spelled out so the model uses THESE values, not memory.
 * Returns '' for an empty set (caller omits the block). Trailing newline mirrors
 * renderUpcomingEconBlock / renderWorldStateGrounding for easy concatenation.
 */
export function renderMacroGroundingBlock(records: ReadonlyArray<MacroGroundTruth>): string {
  if (records.length === 0) return '';
  const lines = records.map((r) => {
    if (r.transform === 'yoy' && r.yoy_pct !== null) {
      return `  ${r.label}: ${r.yoy_pct.toFixed(1)}% YoY (latest index ${r.latest_value} as of ${r.latest_date})`;
    }
    return `  ${r.label}: ${r.latest_value}${r.unit === 'percent' ? '%' : ''} (as of ${r.latest_date})`;
  }).join('\n');
  return (
    'GROUNDED MACRO ACTUALS (authoritative — use THESE exact figures; do NOT recall macro '
    + 'numbers from memory. NO analyst consensus/estimate data exists, so NEVER frame a print '
    + "'vs consensus'/'forecast'/'beat'/'missed'/'surprise' or state a forecast number):\n"
    + lines
    + '\n'
  );
}
